"use client";
import Image from "next/image";
import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { TimeIcon } from "~/components/icons";
import BlogCard from "~/components/reusables/blog-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import SubscribeCard from "~/components/subscribe-card-client";
import { getFeatureImage } from "~/components/reusables/blog-card";
import PaginationComponent from "~/components/reusables/pagination-component";

const CategoryPages = ({
  blogs,
  featuredBlog,
  subscribeCard,
  blogCategory,
}) => {
  const blogListRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [initialLoad, setInitialLoad] = useState(true);
  const [sortOption, setSortOption] = useState("Latest");
  const blogsPerPageFirst = 4;
  const blogsPerPageOther = 6;

  useEffect(() => {
    // Reset page to 1 if the category changes
    setCurrentPage(1);
    
    // Optional: If you still want to load from storage, 
    // ensure the storage is specific to this category
    const savedPage = localStorage.getItem(`currentPage_${blogCategory?.key}`);
    if (savedPage) {
      const p = parseInt(savedPage, 10);
      if (p <= totalPages) setCurrentPage(p);
    }
  }, [blogCategory?.key]);

  // Function to sort blogs
  const sortBlogs = (blogs, sortOption) => {
    return blogs.sort((a, b) => {
      const dateA = new Date(a.publishDate).getTime();
      const dateB = new Date(b.publishDate).getTime();
      return sortOption === "Latest" ? dateB - dateA : dateA - dateB;
    });
  };

  // Sorted blogs
  const sortedBlogs = sortBlogs([...blogs], sortOption);

  // Calculate total pages
  const additionalPages =
    blogs.length > blogsPerPageFirst
      ? Math.ceil((blogs.length - blogsPerPageFirst) / blogsPerPageOther)
      : 0;
  const totalPages = 1 + additionalPages;

  // Get current page blogs
  let currentBlogs = [];
  if (currentPage === 1) {
    currentBlogs = sortedBlogs.slice(0, blogsPerPageFirst);
  } else {
    const start = blogsPerPageFirst + (currentPage - 2) * blogsPerPageOther;
    const end = start + blogsPerPageOther;
    currentBlogs = sortedBlogs.slice(start, end);
  }

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setInitialLoad(false);
   localStorage.setItem(`currentPage_${blogCategory?.key}`, pageNumber.toString());
  };
  // Handle sort option change
  const handleSortChange = (value) => {
    setSortOption(value);
    setCurrentPage(1);
    if (typeof window !== "undefined") {
      localStorage.setItem("currentPage", "1");
    }
  };

  useEffect(() => {
    if (blogListRef.current && !initialLoad) {
      blogListRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [currentPage]);

  return (
    <div ref={blogListRef} className="container my-[60px]">
      <div className="container relative">
        <div
          className="absolute bottom-[-150px] left-0 z-10 w-[1px] bg-rainbow-b2"
          style={{
            height: "calc(100% + 260px)",
          }}
        ></div>
        <div
          className="z-1 absolute bottom-[-1px] right-0 h-[1px]  bg-rainbow-r2"
          style={{
            width: "calc(100% + 40px)",
          }}
        ></div>
        <Image
          unoptimized
          src="/images/graphics/middle-diamond.png"
          alt="Diamond"
          width="0"
          height="0"
          className="absolute bottom-[-8px] left-[-7px] z-[11] w-[16px]"
        />
        <h6 className="w-full border-b-2 border-black py-3 text-[11px] font-bold md:text-[12px] lg:text-[13px]">
          SHOWING ARTICLES ABOUT
        </h6>
        <div className="flex flex-col justify-between gap-5 pb-12 pt-3 lg:flex-row">
          <h1 className="font-hero-title">{blogCategory?.title}</h1>
          {blogCategory?.description && (
            <p className="font-p mt-2 max-w-[700px]">
              {blogCategory?.description}
            </p>
          )}
        </div>

        {currentPage == 1 && (
          <Link className="group ml-10" href={`/blog/${featuredBlog.featuredBlogCategory?.key?.toLowerCase()}/${featuredBlog.key.toLowerCase()}`}>
            <div className="relative grid grid-cols-1 py-10 lg:grid-cols-3">
              <div className="col-span-1 lg:col-span-2">
                <div className="flex justify-between">
                  <div className="mb-5 flex w-full justify-between gap-3">
                    <div className="flex gap-3">
                      <Image
                        unoptimized
                        src={featuredBlog.featuredBlogCategoryIcon}
                        alt="Icon"
                        width="25"
                        height="25"
                      />
                      <h6 className="font-h6 uppercase">
                        {featuredBlog.featuredBlogCategory?.title}
                      </h6>
                    </div>
                    <div className="flex items-center gap-5">
                      <TimeIcon size={20} />
                      <h6 className="font-p">{featuredBlog.readingTime}</h6>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-span-1"></div>
              <div className="col-span-1 lg:col-span-2">
                <div className="relative aspect-square overflow-hidden rounded-lg md:aspect-video md:min-h-[350px]">
                  <Image
                    unoptimized
                    src={
                      featuredBlog.featuredBlogThumbnail
                        ? featuredBlog.featuredBlogThumbnail.url
                        : getFeatureImage(
                            featuredBlog.featuredBlogCategory?.key,
                            featuredBlog.key,
                          )
                    }
                    alt="Featured home blog"
                    className="rounded-xl object-cover transition-all duration-700 group-hover:scale-105"
                    fill
                    sizes="100%"
                  />
                </div>
                <div className="mt-5 flex items-center gap-5">
                  <Avatar className="h-[50px] w-[50px]">
                    <AvatarImage
                      className="overflow-visible object-cover"
                      style={{ overflowClipMargin: "unset" }}
                      src={featuredBlog.author_avatar}
                    />
                    <AvatarFallback>CN</AvatarFallback>
                  </Avatar>
                  <h4 className="font-p">{featuredBlog.author_name}</h4>
                </div>
              </div>
              <div className="col-span-1 lg:pl-10">
                <h2 className="font-h3">{featuredBlog.title}</h2>
                <p className="font-p mt-5">{featuredBlog.description}</p>
              </div>
            </div>
          </Link>
        )}
      </div>
      <div className="container relative my-[50px]">
        <div className="flex flex-col gap-5 md:flex-row md:items-center">
          <h5>Sort results by</h5>
          <Select onValueChange={handleSortChange}>
            <SelectTrigger className="h-[50px] w-[300px] border-black">
              <SelectValue placeholder="Latest" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="Latest">Latest</SelectItem>
              <SelectItem value="Earliest">Earliest</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-[50px] grid grid-cols-1 gap-5 md:mt-[80px] md:grid-cols-2 md:gap-[38px] lg:grid-cols-3 lg:gap-[70px]">
          {currentBlogs.map((blog, i) => (
            <React.Fragment key={`${i}-card`}>
              <div className="relative min-h-[380px] md:min-h-[520px]" key={i}>
                <BlogCard blog={blog} size="md" />
              </div>
              {i == 1 && currentPage == 1 && (
                <div
                  key={`${i}-card`}
                  className="z-2 relative row-span-2 hidden -translate-y-12 justify-center overflow-hidden lg:block"
                >
                  <SubscribeCard
                    subscribeCard={subscribeCard}
                    size="md"
                    segmentOpt={{
                      link_label: "Subscribe to our newsletter",
                      location: "body",
                      location_detail: "right-body",
                      resource_interaction: "view newsletter subscription form",
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        {totalPages > 1 && (
          <PaginationComponent
            totalPages={totalPages}
            currentPage={currentPage}
            paginate={paginate}
          />
        )}
      </div>
    </div>
  );
};

export default CategoryPages;
